import { UseSuspenseQueryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { Id, PagedParams, PagedResponse, getQueryKey } from '@feature/utils';

import * as api from './CitationApi';
import { Citation, CitationFilters, CitationRequest } from './CitationTypes';

const citationKey = getQueryKey('citation');

export function getCitationPagedOptions(
  publicationId: Id,
  params: PagedParams<CitationFilters>,
): UseSuspenseQueryOptions<PagedResponse<Citation>> {
  return {
    queryKey: citationKey.paged(params),
    queryFn: () => api.getPaged(publicationId, params),
  };
}

export function getCitationOptions(publicationId: Id, id: Id): UseSuspenseQueryOptions<Citation> {
  return {
    queryKey: citationKey.one(id),
    queryFn: () => api.get(publicationId, id),
  };
}

export function useCitationEdit(publicationId: Id, id: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: CitationRequest) => api.edit(publicationId, id, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: citationKey.root(),
      }),
  });
}

export function useCitationCreate(publicationId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: CitationRequest) => api.create(publicationId, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: citationKey.root(),
      }),
  });
}

export function useCitationDelete(publicationId: Id, id: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.deleteById(publicationId, id),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: citationKey.root(),
      }),
  });
}
